import { useEffect, useState } from "react";

interface TimerBarProps {
  requestId: string | null;
  isRunning: boolean;
  getElapsedMs: () => number;
  onPause: () => void;
  onResume: () => void;
}

function formatElapsed(ms: number): string {
  const totalSecs = Math.floor(ms / 1000);
  const hours = Math.floor(totalSecs / 3600);
  const mins = Math.floor((totalSecs % 3600) / 60);
  const secs = totalSecs % 60;

  if (hours > 0) {
    return `${hours}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  }
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

export function TimerBar({
  requestId,
  isRunning,
  getElapsedMs,
  onPause,
  onResume,
}: TimerBarProps) {
  const [display, setDisplay] = useState("00:00");

  // biome-ignore lint/correctness/useExhaustiveDependencies: isRunning triggers immediate display update on pause/resume
  useEffect(() => {
    if (!requestId) return;

    const update = () => setDisplay(formatElapsed(getElapsedMs()));
    update();

    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [requestId, isRunning, getElapsedMs]);

  if (!requestId) return null;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        padding: "var(--panel-spacing-sm) var(--panel-spacing-md)",
        background: "var(--panel-surface)",
        borderTop: "1px solid var(--panel-border)",
      }}
      data-testid="panel-timer-bar"
    >
      {/* Pulsing dot */}
      <div
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: isRunning
            ? "var(--panel-success)"
            : "var(--panel-warning)",
          flexShrink: 0,
          marginRight: 8,
        }}
      />

      <span
        style={{
          fontFamily: "'Courier New', monospace",
          fontWeight: 600,
          fontSize: "var(--panel-font-size-lg)",
          flex: 1,
        }}
        data-testid="panel-timer-display"
      >
        {display}
      </span>

      <span
        style={{
          fontSize: "var(--panel-font-size-xs)",
          color: "var(--panel-text-muted)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: 100,
          marginRight: 8,
        }}
        data-testid="panel-timer-request-id"
      >
        {requestId}
      </span>

      <button
        className="hops-btn hops-btn-ghost hops-btn-sm"
        onClick={isRunning ? onPause : onResume}
        style={{ flexShrink: 0 }}
        data-testid="panel-timer-toggle"
      >
        {isRunning ? "Pause" : "Resume"}
      </button>
    </div>
  );
}
